import { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { SaveButton, SortableList } from "../../components/admin/fields";

export default function RecentShowsPage() {
  const [all, setAll] = useState([]);
  const [selected, setSelected] = useState([]);
  const [initialIds, setInitialIds] = useState([]);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get("/admin/exhibitions").then((res) => {
      const published = (res.data || []).filter((e) => e.status === "published");
      setAll(published);
      const sel = published.filter((e) => e.show_in_recent);
      setSelected(sel);
      setInitialIds(sel.map((e) => e.id));
    }).catch(() => {});
  }, []);

  const available = all.filter((e) => !selected.find((s) => s.id === e.id));

  const save = async () => {
    setBusy(true);
    setSaved(false);
    try {
      for (let i = 0; i < selected.length; i += 1) {
        await api.put(`/admin/exhibitions/${selected[i].id}`, { ...selected[i], order: i + 1, show_in_recent: true });
      }
      for (const id of initialIds) {
        if (!selected.find((s) => s.id === id)) {
          const ex = all.find((e) => e.id === id);
          if (ex) await api.put(`/admin/exhibitions/${id}`, { ...ex, show_in_recent: false });
        }
      }
      setInitialIds(selected.map((e) => e.id));
      setSaved(true);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl" data-testid="admin-recent-shows">
      <h1 className="h-condensed text-4xl">Recent Shows Bar</h1>
      <p className="mt-3 text-sm font-light opacity-70">
        Pick up to 6 published exhibitions and drag them into order. The bar shows {selected.length} of 6.
      </p>

      <div className="mt-8 grid gap-10 lg:grid-cols-2">
        <div>
          <h2 className="eyebrow text-[0.65rem]">Selected (drag to order)</h2>
          <div className="mt-4 border-t border-charcoal">
            <SortableList
              items={selected}
              onChange={setSelected}
              keyFn={(e) => e.id}
              testid="recent-selected-list"
              renderItem={(ex, i) => (
                <div className="flex items-center gap-4 border-b border-charcoal py-3" data-testid={`recent-selected-${ex.slug}`}>
                  <span className="h-condensed w-6 text-lg">{i + 1}</span>
                  {ex.card_image && <img src={ex.card_image} alt="" className="h-12 w-9 object-cover" />}
                  <span className="h-condensed flex-1 text-lg">{ex.title}</span>
                  <button type="button" onClick={() => setSelected(selected.filter((s) => s.id !== ex.id))} className="link-arrow text-xs" data-testid={`recent-remove-${ex.slug}`}>
                    Remove
                  </button>
                </div>
              )}
            />
            {selected.length === 0 && <p className="py-6 text-sm font-light opacity-60">Nothing selected yet.</p>}
          </div>
        </div>
        <div>
          <h2 className="eyebrow text-[0.65rem]">Available published exhibitions</h2>
          <div className="mt-4 border-t border-charcoal">
            {available.map((ex) => (
              <div key={ex.id} className="flex items-center gap-4 border-b border-charcoal py-3" data-testid={`recent-available-${ex.slug}`}>
                {ex.card_image && <img src={ex.card_image} alt="" className="h-12 w-9 object-cover" />}
                <span className="h-condensed flex-1 text-lg">{ex.title}</span>
                <button
                  type="button"
                  disabled={selected.length >= 6}
                  onClick={() => setSelected([...selected, ex])}
                  className="link-arrow text-xs disabled:opacity-40"
                  data-testid={`recent-add-${ex.slug}`}
                >
                  {selected.length >= 6 ? "Bar full" : "Add"}
                </button>
              </div>
            ))}
            {available.length === 0 && <p className="py-6 text-sm font-light opacity-60">No more published exhibitions.</p>}
          </div>
        </div>
      </div>

      <div className="mt-10">
        <h2 className="eyebrow text-[0.65rem]">Live preview</h2>
        <div className="mt-4 flex gap-6 overflow-x-auto border border-charcoal p-6" data-testid="recent-preview">
          {selected.map((ex) => (
            <div key={ex.id} className="w-40 shrink-0">
              <div className="overflow-hidden rounded-t-[999px]">
                <img src={ex.card_image} alt={ex.title} className="aspect-[4/5] w-full object-cover" />
              </div>
              <p className="h-condensed mt-3 text-sm">{ex.title}</p>
              <p className="mt-0.5 text-xs font-light">{ex.gallery}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 flex items-center gap-4 border-t border-charcoal pt-6">
        <SaveButton busy={busy} dirty testid="recent-shows-save" />
        {saved && <span className="text-xs font-light opacity-60" data-testid="recent-saved">Saved — the home page bar is updated.</span>}
      </div>
    </div>
  );
}
